import { create } from "zustand";
import { getStrapiContent } from "@/utils/strapiUtils";
import {
    GetAchievementsQuery,
    GetFinanceTypesQuery,
    GetGameConfigurationQuery,
    GetGameEndQuery,
    GetLegalQuery,
    GetMetaTagsQuery,
    GetPhasesQuery,
} from "@/utils/graphql/generated/graphql";
import { Achievement, FinanceType, GameConfiguration, GameEnd, Legal, MetaTag, Phase } from "@/types/api";

type CacheState = {
    phases: Phase[];
    achievements: Achievement[];
    financeTypes: FinanceType[];
    gameConfiguration: GameConfiguration | null;
    gameEnd: GameEnd | null;
    legal: Legal | null;
    metaTag: MetaTag | null;
    status: "idle" | "loading" | "success" | "error";
    error: Error | null;
};

type CacheActions = {
    fetchData: () => Promise<void>;
};

export const useCacheStore = create<CacheState & CacheActions>((set) => ({
    phases: [],
    achievements: [],
    financeTypes: [],
    gameConfiguration: null,
    gameEnd: null,
    legal: null,
    metaTag: null,
    status: "idle",
    error: null,

    fetchData: async () => {
        set({ status: "loading", error: null });

        try {
            // load gameConfigData first because it contains information needed in the splash screen (e.g. legal text, phase order)
            const gameConfigData = await getStrapiContent<GetGameConfigurationQuery>("gameConfiguration");
            set({ gameConfiguration: gameConfigData.gameConfiguration ?? null });

            const [phasesData, achievementsData, financeTypesData, legalData, metaTagData, gameEnd] = await Promise.all(
                [
                    getStrapiContent<GetPhasesQuery>("phases"),
                    getStrapiContent<GetAchievementsQuery>("achievements"),
                    getStrapiContent<GetFinanceTypesQuery>("financeTypes"),
                    getStrapiContent<GetLegalQuery>("legal"),
                    getStrapiContent<GetMetaTagsQuery>("metaTag"),
                    getStrapiContent<GetGameEndQuery>("gameEnd"),
                ],
            );

            const phases = phasesData.phases.filter((phase): phase is Phase => phase !== null);

            const achievements = achievementsData.achievements.filter(
                (achievement): achievement is Achievement => achievement !== null,
            );

            const financeTypes = financeTypesData.financeTypes.filter((ft): ft is FinanceType => ft != null);

            const gameConfiguration = gameConfigData.gameConfiguration ?? null;
            const legal = legalData.legal ?? null;
            const metaTag = metaTagData.metaTag ?? null;

            let sortedPhases = phases;
            if (gameConfiguration) {
                const orderMap = new Map(
                    gameConfiguration.PhaseOrder.map((phase, index) => [phase?.documentId, index]),
                );

                sortedPhases = [...phases].sort(
                    (a, b) => (orderMap.get(a.documentId) ?? Infinity) - (orderMap.get(b.documentId) ?? Infinity),
                );
            }

            set({
                phases: sortedPhases,
                achievements,
                financeTypes,
                legal,
                metaTag,
                gameEnd: gameEnd.gameEnd,
                status: "success",
            });
        } catch (error) {
            set({
                status: "error",
                error: error instanceof Error ? error : new Error("Failed to fetch data"),
            });
        }
    },
}));
